import { Injectable } from '@angular/core';

/**
 * localStorage-backed store for hecate-issued JWTs (CN-8597 Phase 3).
 *
 * Per Decisions §1 of docs/plans/PLAN_HECATE_AUTH_MIGRATION.md, v1 uses
 * localStorage. XSS exfiltration risk is accepted; a future migration to
 * httpOnly cookies is tracked as Future Work §3.
 *
 * Stores three values:
 *   - access_token  (JWT — Bearer header on every c4 API call)
 *   - refresh_token (used by TokenRefreshService to mint a new access_token)
 *   - expires_at    (epoch ms — TokenRefreshService refreshes ~60s ahead of this)
 */
@Injectable({ providedIn: 'root' })
export class TokenStoreService {

	private static readonly ACCESS_TOKEN_KEY = 'hecate.access_token';
	private static readonly REFRESH_TOKEN_KEY = 'hecate.refresh_token';
	private static readonly EXPIRES_AT_KEY = 'hecate.expires_at';

	/**
	 * Persists tokens from an /oauth/token success response. `expiresInSeconds`
	 * is hecate's `expires_in` field (seconds until access-token expiry); we
	 * convert to an absolute epoch ms so the interceptor doesn't need to track
	 * the issue time separately.
	 */
	setTokens(accessToken: string, refreshToken: string, expiresInSeconds: number): void {
		localStorage.setItem(TokenStoreService.ACCESS_TOKEN_KEY, accessToken);
		localStorage.setItem(TokenStoreService.REFRESH_TOKEN_KEY, refreshToken);
		const expiresAt = Date.now() + expiresInSeconds * 1000;
		localStorage.setItem(TokenStoreService.EXPIRES_AT_KEY, String(expiresAt));
	}

	getAccessToken(): string | null {
		return localStorage.getItem(TokenStoreService.ACCESS_TOKEN_KEY);
	}

	getRefreshToken(): string | null {
		return localStorage.getItem(TokenStoreService.REFRESH_TOKEN_KEY);
	}

	getExpiresAt(): number | null {
		const raw = localStorage.getItem(TokenStoreService.EXPIRES_AT_KEY);
		return raw === null ? null : Number(raw);
	}

	/**
	 * True if we have an access token AND it is currently within `leewayMs`
	 * of expiry (or already past). Used by the interceptor to proactively
	 * refresh before sending a request rather than waiting for a 401.
	 */
	isExpiringSoon(leewayMs: number = 60_000): boolean {
		const expiresAt = this.getExpiresAt();
		if (expiresAt === null) {
			return false;
		}
		return Date.now() + leewayMs >= expiresAt;
	}

	/** True iff an access token is currently stored. */
	hasAccessToken(): boolean {
		return this.getAccessToken() !== null;
	}

	clear(): void {
		localStorage.removeItem(TokenStoreService.ACCESS_TOKEN_KEY);
		localStorage.removeItem(TokenStoreService.REFRESH_TOKEN_KEY);
		localStorage.removeItem(TokenStoreService.EXPIRES_AT_KEY);
	}
}
